import * as moment from 'moment';
import { AppConsts } from '@shared/AppConsts';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Type, CompilerOptions, NgModuleRef } from '@angular/core';
import { environment } from './environments/environment';
import { addScriptElementToDOM } from '@shared/utilities/utilities';
import { IGoogleSettings } from '@shared/interfaces/IGoogleSettings';
import LogRocket from 'logrocket';

export class AppPreBootstrap {
  public static run(appRootUrl: string, callback: () => void): void {
    AppPreBootstrap.getApplicationConfig(appRootUrl, () => {
      AppPreBootstrap.getUserConfiguration(callback);
      if (environment.production) {
        if (AppConsts.logRocketAppId != null && AppConsts.logRocketAppId.trim() !== '') {
          LogRocket.init(AppConsts.logRocketAppId, {
            release: environment.version,
            dom: {
              inputSanitizer: true
            },
            network: {
              isEnabled: false
            }
          });
        } else {
          console.error('Could not obtain App ID!');
        }
      }
    });
  }

  public static bootstrap<TM>(moduleType: Type<TM>, compilerOptions?: CompilerOptions | CompilerOptions[]): Promise<NgModuleRef<TM>> {
    return platformBrowserDynamic().bootstrapModule(moduleType, compilerOptions);
  }

  private static getApplicationConfig(appRootUrl: string, callback: () => void) {
    return abp.ajax({
      url: appRootUrl + 'assets/' + environment.appConfig,
      method: 'GET',
      headers: {
        'Abp.TenantId': abp.multiTenancy.getTenantIdCookie()
      }
    }).done(result => {
      AppConsts.appName = result.appName;
      AppConsts.appBaseUrl = result.appBaseUrl;
      AppConsts.remoteServiceBaseUrl = result.remoteServiceBaseUrl;
      AppConsts.localeMappings = result.localeMappings;
      AppConsts.googleSettings = result.googleSettings;
      AppConsts.defaultMapSettings = result.defaultMapSettings;
      AppConsts.logRocketAppId = result.logRocketAppId;

      // We're adding the google maps API here in the AppPreBootstrap (instead of index.html) because
      // we are getting the api key from the appconfig.
      this.loadGoogleMapsScripts(AppConsts.googleSettings);

      callback();
    });
  }

  private static getCurrentClockProvider(currentProviderName: string): abp.timing.IClockProvider {
    if (currentProviderName === 'unspecifiedClockProvider') {
      return abp.timing.unspecifiedClockProvider;
    }

    if (currentProviderName === 'utcClockProvider') {
      return abp.timing.utcClockProvider;
    }

    return abp.timing.localClockProvider;
  }

  private static getUserConfiguration(callback: () => void): JQueryPromise<any> {
    return abp.ajax({
      url: AppConsts.remoteServiceBaseUrl + '/AbpUserConfiguration/GetAll',
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + abp.auth.getToken(),
        '.AspNetCore.Culture': abp.utils.getCookieValue('Abp.Localization.CultureName'),
        'Abp.TenantId': abp.multiTenancy.getTenantIdCookie()
      }
    }).done(result => {
      $.extend(true, abp, result);

      abp.clock.provider = this.getCurrentClockProvider(result.clock.provider);

      moment.locale(abp.localization.currentLanguage.name);

      if (abp.clock.provider.supportsMultipleTimezone) {
        moment.tz.setDefault(abp.timing.timeZoneInfo.iana.timeZoneId);
      }

      callback();
    });
  }

  private static loadGoogleMapsScripts(config: IGoogleSettings): void {
    // The includes depends on the google API. This function will execute once the google API load completes
    const loadIncludes: (() => void) = () => {
      config.include.forEach((src: string) => {
        addScriptElementToDOM(src, 'text/javascript');
      });
    };

    // Add google maps API and pass the loadIncludes function
    addScriptElementToDOM(`${config.url}?key=${config.key}&v=${config.v}&libraries=${config.libraries}`,
      null,
      loadIncludes
    );
  }
}
